import React, { useEffect } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { getSeoData } from '../data/seoData';

/**
 * Centralized SEO Component (React 19 native metadata).
 * React 19 automatically hoists <title>, <meta> and <link> tags into <head>,
 * works in dev, production build and react-snap prerender (no Helmet needed).
 * Usage: <SEOHead page="home" customTitle="..." customDescription="..." />
 */
const LOCALES = ['en', 'fr', 'es', 'de', 'it', 'pt', 'ar', 'ru', 'zh', 'hi', 'ja'];

const SEOHead = ({
  page = 'home',
  customTitle = null,
  customDescription = null,
  customImage = null,
  customKeywords = null,
  noindex = false
}) => {
  const { locale, isRTL } = useLocale();

  const seoPage = getSeoData(page, locale) || {};

  const title = customTitle || seoPage.title;
  const description = customDescription || seoPage.description;
  const keywords = customKeywords || seoPage.keywords;
  const ogImage = customImage || seoPage.ogImage;

  const baseUrl = 'https://digitalcorpfrance.com';
  const canonicalPath = page === 'home' ? '' : page;
  const canonicalUrl = `${baseUrl}/${locale}${canonicalPath ? '/' + canonicalPath : ''}`;

  const hreflangs = LOCALES.map(lang => ({
    lang,
    url: `${baseUrl}/${lang}${canonicalPath ? '/' + canonicalPath : ''}`
  }));

  // React 19 native metadata does not manage <html> attributes; set them here.
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  }, [locale, isRTL]);

  return (
    <>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="language" content={locale} />
      <meta name="author" content="Digital Corp France" />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang Alternates */}
      {hreflangs.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${baseUrl}/en${canonicalPath ? '/' + canonicalPath : ''}`}
      />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:locale" content={locale === 'en' ? 'en_US' : `${locale}_${locale.toUpperCase()}`} />
      <meta property="og:site_name" content="Digital Corp France" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      <meta property="twitter:domain" content="digitalcorpfrance.com" />
    </>
  );
};

export default SEOHead;
